import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/lib/api/client';
import type { NotificationsSnapshot, StaffUpdate } from '../types';

/** How old the snapshot may be before opening the bell asks again (AC2.4). */
const SNAPSHOT_STALE_MS = 30_000;

/**
 * The bell's key tree. Other features invalidate `notificationKeys.all` after a
 * local action that changes an item (AC7.6), importing it through the
 * feature's `index.ts`.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  snapshot: () => [...notificationKeys.all, 'snapshot'] as const,
};

/**
 * Needs action and Updates in one read (`GET /notifications`). `enabled` is
 * false for anyone the bell is not rendered for, so they never fetch (AC1.1).
 */
export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: notificationKeys.snapshot(),
    queryFn: ({ signal }) => api.get<NotificationsSnapshot>('/notifications', { signal }),
    enabled,
    staleTime: SNAPSHOT_STALE_MS,
  });
}

/** Opening the bell refetches only a snapshot older than 30 s (AC2.4). */
export function useRefreshNotificationsIfStale() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.refetchQueries({ queryKey: notificationKeys.snapshot(), stale: true, type: 'active' });
  }, [qc]);
}

/** Mark one Update read (AC5.1); another account's id is a silent no-op server-side. */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updateId: string) =>
      api.post<{ marked: number }>(`/notifications/updates/${encodeURIComponent(updateId)}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark every Update read up to `upTo`, the newest `createdAt` the viewer has
 * seen (see `newestSeen`), so a row pushed after the list was drawn stays
 * unread (AC5.2).
 */
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (upTo: string) => api.post<{ marked: number }>('/notifications/updates/read-all', { upTo }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** The newest `createdAt` among the Updates on screen; null when there are none. */
export function newestSeen(updates: readonly StaffUpdate[]): string | null {
  let newest: string | null = null;
  for (const u of updates) {
    if (newest === null || Date.parse(u.createdAt) > Date.parse(newest)) newest = u.createdAt;
  }
  return newest;
}
